import {
  TicketEventId,
  TicketId,
  TicketOrder,
  TicketOrderId,
} from "@greeter/core";
import { IVenueApi } from "@greeter/venue-api";
import {
  createFetchMachine,
  FetchActorFields,
  unwrapData,
} from "@greeter/xstate-util";
import { assign, setup } from "xstate";
import Fuse from "fuse.js";
import { Log } from "@greeter/log";
import { indexById } from "@greeter/map";

export type TicketOverviewApiRequirements = Pick<
  IVenueApi,
  | "fetchTickets"
  | "fetchTicketOrder"
  | "fetchTicketOrders"
  | "fetchTicketEvent"
  | "fetchTicketEvents"
  | "saveTicket"
  | "saveTickets"
>;

export type TicketOverviewMachineEvents =
  | {
      type: "focus";
      data: TicketOrderId;
    }
  | { type: "unfocus" }
  | { type: "initialize"; data?: TicketEventId }
  | { type: "checkIn"; data: TicketId }
  | { type: "checkAllIn"; data: TicketOrderId }
  | { type: "set.search"; data: string }
  | { type: "set.ticketOrders"; data: Array<TicketOrder> }
  | { type: "set.ticketOrder"; data: TicketOrder };

export type TicketOverviewMachineContext = {
  api: FetchActorFields<TicketOverviewApiRequirements>;
  focusedTicketOrderId?: string;
  search: string;
  fuse: Fuse<TicketOrder>;
  ticketOrder?: TicketOrder;
  ticketOrders: Map<TicketOrderId, TicketOrder>;
};
export type TicketOverviewMachineInput = {
  api: TicketOverviewApiRequirements;
};

export const TicketOverviewMachine = setup({
  types: {} as {
    context: TicketOverviewMachineContext;
    input: TicketOverviewMachineInput;
    events: TicketOverviewMachineEvents;
  },
}).createMachine({
  context({ input, spawn, self }) {
    const fetchTicketsRef = spawn(
      createFetchMachine(input.api.fetchTickets.bind(input.api)),
      { syncSnapshot: true }
    );
    const fetchTicketEventsRef = spawn(
      createFetchMachine(input.api.fetchTicketEvents.bind(input.api))
    );
    const fetchTicketOrderRef = spawn(
      createFetchMachine(input.api.fetchTicketOrder.bind(input.api)),
      { syncSnapshot: true }
    );
    fetchTicketOrderRef.on("error", (e) =>
      Log.error("[TicketOverviewMachine][fetchTicketOrderRef]", { error: e })
    );
    fetchTicketOrderRef.on("fetched", ({ data }) => {
      self.send({ type: "set.ticketOrder", data: data });
    });
    const fetchTicketOrdersRef = spawn(
      createFetchMachine(input.api.fetchTicketOrders.bind(input.api)),
      { syncSnapshot: true }
    );
    const saveTicketRef = spawn(
      createFetchMachine(input.api.saveTicket.bind(input.api)),
      { syncSnapshot: true }
    );
    const saveTicketsRef = spawn(
      createFetchMachine(input.api.saveTickets.bind(input.api)),
      { syncSnapshot: true }
    );

    function fetchTickets() {
      const context = self.getSnapshot().context;
      if (!context.focusedTicketOrderId) {
        Log.debug(
          "[TicketOverviewMachine] no focused id, skipping fetching tickets"
        );
        return;
      }
      fetchTicketsRef.send({
        type: "fetch",
        data: [{ ticketOrderId: context.focusedTicketOrderId }],
      });
      fetchTicketOrderRef.send({
        type: "fetch",
        data: [{ id: context.focusedTicketOrderId }],
      });
    }

    saveTicketRef.on("fetched", fetchTickets);
    saveTicketsRef.on("fetched", fetchTickets);

    const fuse = new Fuse<TicketOrder>([], {
      keys: ["customerName", "customerEmail", "id"],
    });
    fetchTicketOrdersRef.on("fetched", (r) => {
      fuse.setCollection(r.data);
      if (self.getSnapshot().context.search.length >= 2) {
        self.send({
          type: "set.ticketOrders",
          data: fuse
            .search(self.getSnapshot().context.search)
            .map((i) => i.item),
        });
      } else {
        self.send({ type: "set.ticketOrders", data: r.data });
      }
    });

    const fetchTicketEventRef = spawn(
      createFetchMachine(input.api.fetchTicketEvent.bind(input.api)),
      { syncSnapshot: true }
    );

    self.send({ type: "initialize" });

    return {
      api: {
        fetchTicketsRef,
        fetchTicketEventRef,
        fetchTicketEventsRef,
        fetchTicketOrdersRef,
        fetchTicketOrderRef,
        saveTicketRef,
        saveTicketsRef,
      },
      fuse: fuse,
      search: "",
      ticketOrders: new Map(),
    };
  },
  on: {
    initialize: {
      actions: ({
        event,
        context: {
          api: { fetchTicketEventRef, fetchTicketOrdersRef },
        },
      }) => {
        const ticketEventId =
          event.data ?? sessionStorage.getItem("ticketEventId");
        if (!ticketEventId) return;

        fetchTicketOrdersRef.send({
          type: "fetch",
          data: [{ ticketEventId }],
        });
        fetchTicketEventRef.send({
          type: "fetch",
          data: [{ id: ticketEventId }],
        });
      },
    },
    focus: {
      actions: [
        assign({
          focusedTicketOrderId: ({ context, event }) =>
            context.focusedTicketOrderId === event.data
              ? undefined
              : event.data,
        }),
        ({
          event,
          context: {
            api: { fetchTicketsRef },
          },
        }) =>
          fetchTicketsRef.send({
            type: "fetch",
            data: [{ ticketOrderId: event.data }],
          }),
      ],
    },
    unfocus: {
      actions: assign({ focusedTicketOrderId: undefined }),
    },
    checkIn: {
      actions: [
        ({ context, event }) => {
          context.api.saveTicketRef.send({
            type: "fetch",
            data: [{ id: event.data, checkedIn: true }],
          });
        },
      ],
    },
    checkAllIn: {
      actions: [
        async ({ context }) => {
          const tickets =
            context.api.fetchTicketsRef.getSnapshot().context.data ?? [];
          context.api.saveTicketsRef.send({
            type: "fetch",
            data: [
              tickets.map((t) => ({
                id: t.id,
                checkedIn: true,
              })),
            ],
          });
        },
      ],
    },
    "set.search": {
      actions: assign(({ context, event }) => {
        if (event.data.length >= 2) {
          const s = context.fuse.search(event.data);
          return {
            search: event.data,
            ticketOrders: indexById(s.map((r) => r.item)),
          };
        }
        return {
          ticketOrders: indexById(
            context.api.fetchTicketOrdersRef.getSnapshot().context.data ?? []
          ),
          search: event.data,
        };
      }),
    },
    "set.ticketOrder": {
      actions: assign({
        ticketOrder: unwrapData,
        ticketOrders: ({ event, context }) => {
          const copy = new Map(context.ticketOrders);
          copy.set(event.data.id, event.data);
          context.fuse.setCollection(Array.from(copy.values()));
          return copy;
        },
      }),
    },
    "set.ticketOrders": {
      actions: assign({
        ticketOrders: ({ event }) => indexById(event.data),
      }),
    },
  },
});
